import { useState } from 'react'
import { toast } from 'sonner'
import { SortableList } from '@/components/common/SortableList'
import { BoolIcon } from '@/components/common/BoolIcon'
import { AddButton } from '@/components/common/AddButton'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useDeleteDocumentType, useDocumentTypes, useReorderDocumentTypes } from './hooks'
import type { DocumentType } from './types'
import { DocumentTypeFormDialog } from './components/DocumentTypeFormDialog'

export function DocumentTypesPanel({ configurationCode }: { configurationCode: string }) {
  const confirm = useConfirm()
  const typesQ = useDocumentTypes(configurationCode, false)
  const del = useDeleteDocumentType(configurationCode)
  const reorder = useReorderDocumentTypes(configurationCode)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<DocumentType | null>(null)

  return (
    <div className="flex flex-col gap-4 text-sm">
      <header className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Typy dokumentów wymaganych dla tego produktu (np. "Skan umowy", "Pozwolenie").
          Kolejność na liście = kolejność wyświetlania w formularzu uploadu.
        </p>
        <AddButton onClick={() => setCreating(true)}>Dodaj typ</AddButton>
      </header>

      <SortableList
        items={typesQ.data ?? []}
        getId={(t) => t.id}
        onReorder={(ids) => reorder.mutate(ids.map(Number))}
        empty={<p className="text-xs text-slate-400 italic">Brak typów - dodaj pierwszy.</p>}
        renderItem={(t) => (
          <div className="flex items-center gap-3 text-xs">
            <span className="font-mono text-slate-500 w-40 shrink-0 truncate">{t.code}</span>
            <span className="flex-1 truncate" title={t.description ?? ''}>{t.labelPl}</span>
            <BoolIcon value={t.isActive} />
            <button onClick={() => setEditing(t)} className="text-slate-600 px-1 hover:text-sky-700">Edytuj</button>
            <button
              onClick={async () => {
                const ok = await confirm({
                  title: `Usunąć typ ${t.code}?`,
                  description: 'Operacja jest nieodwracalna.',
                  confirmLabel: 'Usuń',
                  variant: 'destructive',
                })
                if (ok) del.mutate(t.id, { onError: (err) => toast.error(formatError(err)) })
              }}
              className="text-red-600 px-1"
            >Usuń</button>
          </div>
        )}
      />

      {(creating || editing) && (
        <DocumentTypeFormDialog
          configurationCode={configurationCode}
          editing={editing}
          onClose={() => { setCreating(false); setEditing(null) }}
        />
      )}
    </div>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd'
}
